<script lang="ts" setup>
import { accountService } from '@/services/accounts/accounts-service';
import { debitNormsService } from '@/services/debit-norms/debit-norms-service';
import { projectKeys } from '@/utils/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { models } from 'cesg-shared';
import { computed } from 'vue';
import { DebitNorms } from '../index';
import type { DebitNormSaveEntry, DebitNormsSaveCallbacks } from '../types';
import DebitNormsSkeleton from './DebitNormsSkeleton.vue';

const props = defineProps<{
  projectId: string;
  mode?: 'read-only' | 'edit';
}>();

const queryClient = useQueryClient();

const debitNormsQueryKeys = computed(() =>
  projectKeys.debitNorms({ projectId: props.projectId }),
);

const debitNormsQuery = useQuery({
  queryKey: debitNormsQueryKeys,
  queryFn: () => debitNormsService.getDebitNormsForProject(props.projectId),
});

type SetProjectDebitNormsBody = Parameters<
  typeof debitNormsService.setProjectDebitNorms
>[1];

const debitNormsMutation = useMutation({
  mutationKey: [
    'debitNormsService',
    'updateProjectDebitNorms',
    props.projectId,
  ],
  mutationFn: async (data: SetProjectDebitNormsBody) =>
    debitNormsService.setProjectDebitNorms(props.projectId, data),
  onSuccess: () =>
    queryClient.invalidateQueries({ queryKey: debitNormsQueryKeys.value }),
});

const accountsQuery = useQuery({
  queryKey: ['debitNormsService', 'getProjectAccounts', props.projectId],
  queryFn: () => accountService.getAll(),
});

const debitNormsWithIsNew = computed(() =>
  (debitNormsQuery.data?.value ?? []).map((e) => ({
    ...e,
    isNew: false as const,
  })),
);

const accounts = computed(() => accountsQuery.data?.value ?? []);
const isFetching = computed(() => debitNormsQuery.isLoading.value);

const handleSave = (
  data: DebitNormSaveEntry[],
  { onSuccess, onError }: DebitNormsSaveCallbacks,
) => {
  const body: models.UpsertDebitNormRequest = data.map((e) => ({
    ...(e.isNew ? {} : { id: e.id }),
    accountNumber: e.account.number,
    rate: e.rate,
    order: e.order,
  }));
  debitNormsMutation.mutate(body, { onSuccess, onError });
};
</script>

<template>
  <DebitNormsSkeleton v-if="isFetching" />
  <DebitNorms
    v-else
    :accounts="accounts"
    :debit-norms="debitNormsWithIsNew"
    :mode="props.mode || 'read-only'"
    @save="handleSave"
  />
</template>
